import React, { ChangeEvent, useState, useEffect, CSSProperties } from 'react';

import styles from './Input.module.scss';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';

interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
    id?: string;
    label?: string;
    error?: string | null;
    placeholder?: string;
    size?: 'small' | 'medium';
    required?: boolean;
    autoFocus?: boolean;
    startIcon?: JSX.Element;
    endIcon?: JSX.Element;
    capitalize?: boolean;
    upperCase?: boolean;
    withoutSpecialCharacters?: boolean;
    autoComplete?: 'off' | 'on';
    disabled?: boolean;
    maxLength?: number;
    className?: string;
    onEnterPressed?: () => void;
    onBlur?: () => void;
    onFocus?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            // Specific props for the component
            id,
            label,
            error,
            size,
            required,
            autoFocus = true,
            startIcon,
            endIcon,
            capitalize,
            upperCase,
            withoutSpecialCharacters,
            className,
            // Props that modify behavior
            value: initialValue,
            disabled,
            maxLength,
            onEnterPressed,
            onChange,
            onBlur,
            onFocus,
            // Native input props
            ...inputProps
        },
        ref
    ) => {
        const [text, setText] = useState(initialValue as string);
        const hasValue = React.useMemo(() => (text?.length || 0) > 0, [text]);

        // Sync internal state with prop changes
        useEffect(() => {
            setText(initialValue as string);
        }, [initialValue]);

        const transformText = React.useCallback(
            (value: string, isLastPositionASpace: boolean): string => {
                let processed = value;

                // Remove special characters if enabled
                if (withoutSpecialCharacters) {
                    processed = processed.replace(/[^a-zA-Z0-9 ]/g, '');
                }

                if (isLastPositionASpace) return processed;
                if (capitalize) return processed.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
                if (upperCase) return processed.toUpperCase();
                return processed;
            },
            [capitalize, upperCase, withoutSpecialCharacters]
        );

        const handleChange = React.useCallback(
            (event: ChangeEvent<HTMLInputElement>): void => {
                if (disabled) return;

                const newValue = event.target.value;
                const isLastPositionASpace = newValue?.endsWith(' ');
                const transformedValue = transformText(newValue, isLastPositionASpace);

                setText(transformedValue);

                if (onChange) {
                    onChange({
                        ...event,
                        target: { ...event.target, value: transformedValue }
                    });
                }
            },
            [disabled, onChange, transformText]
        );

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (disabled) return;
                if (event.key === 'Enter' && onEnterPressed) onEnterPressed();
                if (inputProps.onKeyDown) inputProps.onKeyDown(event);
            },
            [disabled, onEnterPressed, inputProps.onKeyDown]
        );

        const wrapperClasses = React.useMemo(
            () =>
                clsx(styles.inputWrapper, {
                    [styles.inputWrapperWithStartIcon]: startIcon,
                    [styles.inputWrapperWithEndIcon]: endIcon
                }),
            [startIcon, endIcon]
        );

        const inputClasses = React.useMemo(
            () =>
                clsx(styles.input, className, {
                    [styles.inputError]: error,
                    [styles.inputFilled]: hasValue,
                    [styles.small]: size === 'small',
                    [styles.withStartIcon]: startIcon,
                    [styles.withEndIcon]: endIcon,
                    [styles.disabled]: disabled,
                    [styles.notAutoFocus]: autoFocus === false
                }),
            [error, hasValue, size, startIcon, endIcon, disabled, autoFocus, className]
        );

        const inputStyles = React.useMemo(
            (): CSSProperties => ({
                textTransform: (capitalize ? 'capitalize' : upperCase ? 'uppercase' : 'none') as
                    | 'capitalize'
                    | 'uppercase'
                    | 'none',
                ...inputProps.style
            }),
            [capitalize, upperCase, inputProps.style]
        );

        const renderLabel = React.useMemo(() => {
            if (!label && !error) return null;
            return (
                <Label
                    required={required}
                    error={error}
                    htmlFor={id}
                >
                    {label}
                </Label>
            );
        }, [label, required, error, id]);

        return (
            <div className={styles.root}>
                {renderLabel}
                <div className={wrapperClasses}>
                    {startIcon && <span className={styles.icon}>{startIcon}</span>}
                    <input
                        ref={ref}
                        {...inputProps}
                        id={id}
                        disabled={disabled}
                        maxLength={maxLength}
                        value={text}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={inputProps?.placeholder}
                        className={inputClasses}
                        autoComplete={inputProps?.autoComplete}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        style={inputStyles}
                    />
                    {endIcon && <span className={styles.icon}>{endIcon}</span>}
                </div>
            </div>
        );
    }
);

Input.displayName = 'Input';

export { Input };
